import { useDispatch } from 'react-redux';
import { useEffect } from 'react';
import ContactDetails from './ContactDetails/ContactDetails';
import { clearServiceRequests } from 'src/store/actions/serviceRequests';
import ContactTabs from 'src/pages/Contact/ContactTabs';
import ErrorDisplay from 'src/components/ErrorDisplay/ErrorDisplay';
import { ContactFormProvider } from 'src/SharedCommonContext/ContactFormContext';
import { PropertiesForContactsProvider } from 'src/pages/Contacts/ContactsContext/PropertiesForContactsContext';
import {
  sendActivationLink,
  sendResetPasswordLink,
} from 'src/utils/CommonHelpers/ContactAPIOperations/commonContactDataHelpers';
import {
  useContactPageData,
  UseContactPageDataReturn,
} from 'src/pages/Contact/Hooks/useContactPageData';
import WithNavigation from 'src/layouts/WithNavigation/WithNavigation';

interface NavOrigin {
  kind: 'property' | 'suite' | 'serviceRequest';
  propertyId?: string;
  propertyName?: string;
  suiteId?: string;
  suiteNumber?: string;
  serviceRequestId?: string;
}
interface Match {
  params: { id: string };
}
interface Location {
  state?: { isFromDashBoard?: boolean; navOrigin?: NavOrigin };
}
interface ContactProps {
  match: Match;
  location: Location;
  history: History;
}

const Contact = ({ match, location }: ContactProps) => {
  const dispatch = useDispatch();

  const [contactId, parsedNavFrom] = match.params.id.split('&');

  const navFrom: string = parsedNavFrom || '';

  const isFromDashBoard: boolean = !!location?.state?.isFromDashBoard;
  const navOrigin: NavOrigin | undefined = location?.state?.navOrigin;

  const {
    contact,
    contactRole,
    contactError,
    fetchContactById,
    refetchSingleContactById,
    contactCounts,
    countsError,
    documents,
    loadingDocs,
    isSavingDoc,
    loadAllData,
    refetchCounts,
    saveDocument,
    deleteDocument,
    handleSubmitContactForm,
  }: UseContactPageDataReturn = useContactPageData(contactId);

  useEffect(() => {
    const initialPageLoad = async () => {
      dispatch(clearServiceRequests());
      await fetchContactById(contactId);
    };

    void initialPageLoad();
  }, []);

  useEffect(() => {
    if (!contactRole) {
      return;
    }

    void loadAllData();
  }, [contactRole]);

  if (contactError) {
    return (
      <ErrorDisplay
        title="Couldn't Load Contact"
        message="We couldn't load this contact's details. If the issue persists, please contact support."
        onRetry={() => fetchContactById(contactId)}
        buttonText="Try Again"
        colorScheme="red"
      />
    );
  }

  if (countsError && contact) {
    return (
      <ErrorDisplay
        title="Counts Loading Error"
        message={countsError}
        onRetry={() => refetchCounts()}
        buttonText="Retry Counts"
        colorScheme="yellow"
        pageTitle={`${contact.firstname} ${contact.lastname}`}
      />
    );
  }

  if (countsError) {
    return (
      <ErrorDisplay
        title="Contact Error"
        message={countsError}
        onRetry={() => window.location.reload()}
        buttonText="Retry"
        colorScheme="red"
      />
    );
  }

  const loadingContact: boolean = !contact;
  const loadingCounts: boolean = !contactCounts;
  const pageTitle: string = contact
    ? `${contact.firstname} ${contact.lastname}`
    : 'Contact';

  return (
    <ContactFormProvider>
      <PropertiesForContactsProvider>
        <WithNavigation
          title={pageTitle}
          top={
            <ContactDetails
              contactData={contact}
              onSubmitContactForm={handleSubmitContactForm}
              sendResetPasswordLinkToUser={sendResetPasswordLink}
              sendActivationLinkToUser={sendActivationLink}
              navFrom={navFrom}
              navOrigin={navOrigin}
              counts={contactCounts}
              isFromDashBoard={isFromDashBoard}
              refetchContactFormData={refetchSingleContactById}
              loadingContact={loadingContact}
              loadingCounts={loadingCounts}
              contactId={contactId}
            />
          }
          bottom={
            <ContactTabs
              contact={contact}
              counts={contactCounts}
              reFetchContactCountsFromAPI={refetchCounts}
              documents={documents}
              loadingDocs={loadingDocs}
              isSavingDoc={isSavingDoc}
              saveDocument={saveDocument}
              deleteDocument={deleteDocument}
              loadingContact={loadingContact}
              loadingCounts={loadingCounts}
            />
          }
        />
      </PropertiesForContactsProvider>
    </ContactFormProvider>
  );
};

export default Contact;
